import { getUserErrorMessage, readApiErrorMessage } from "../../../pages/Postulaciones/postulacionesUtils";

/**
 * Rotación persistente de documentos del modelo nuevo (job-application-service).
 *
 *   POST {APPLICANT}/application/rotate-document/{personDocumentId}?userId=
 *
 * - PDF: se envía solo el giro; el backend rota todas las páginas con pdf-lib
 *   (sin pérdida).
 * - Imagen (JPG/PNG): se rota aquí con canvas a partir del binario del proxy,
 *   respetando la orientación EXIF (lo mismo que ve el usuario en el visor), y se
 *   envía en el mismo formato. El backend sobrescribe el archivo en su misma ruta.
 */

export interface IDocumentoRotable {
  personDocumentId: string;
  originalFileName: string;
  mimeType: string;
  extension?: string;
}

/**
 * Versión de cada archivo reescrito en esta sesión (clave: id del documento).
 * Los visores la agregan a la URL del preview para no mostrar la copia sin
 * rotar que el navegador pueda tener en caché al reabrir el documento.
 */
const versionesArchivo = new Map<string, number>();
export const versionArchivo = (clave: string): number => versionesArchivo.get(clave) ?? 0;
export const marcarArchivoReescrito = (clave: string): void => {
  versionesArchivo.set(clave, versionArchivo(clave) + 1);
};

/** Lleva un giro acumulado (p. ej. -90 o 450) a 0 | 90 | 180 | 270. */
export const normalizarGiro = (giro: number): number => ((Math.round(giro / 90) * 90) % 360 + 360) % 360;

export const esDocumentoRotable = (mimeType?: string): boolean =>
  mimeType === "application/pdf" || mimeType === "image/jpeg" || mimeType === "image/png";

const cargarImagen = async (blob: Blob): Promise<{ fuente: CanvasImageSource; ancho: number; alto: number; liberar: () => void }> => {
  if (typeof createImageBitmap === "function") {
    try {
      // `imageOrientation` no está en los tipos DOM de TS 4.4.
      const bitmap = await createImageBitmap(blob, { imageOrientation: "from-image" } as unknown as ImageBitmapOptions);
      return { fuente: bitmap, ancho: bitmap.width, alto: bitmap.height, liberar: () => bitmap.close() };
    } catch {
      // Se cae al <img> de abajo.
    }
  }
  const url = URL.createObjectURL(blob);
  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("No se pudo leer la imagen."));
    img.src = url;
  });
  return { fuente: img, ancho: img.naturalWidth, alto: img.naturalHeight, liberar: () => URL.revokeObjectURL(url) };
};

/** Rota una imagen (JPG/PNG) `giro` grados en sentido horario y la reencoda en `mimeType`. */
export const rotarImagen = async (blob: Blob, giro: number, mimeType: string): Promise<Blob> => {
  const { fuente, ancho, alto, liberar } = await cargarImagen(blob);
  try {
    const deLado = giro === 90 || giro === 270;
    const canvas = document.createElement("canvas");
    canvas.width = deLado ? alto : ancho;
    canvas.height = deLado ? ancho : alto;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("El navegador no permite procesar la imagen.");
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((giro * Math.PI) / 180);
    ctx.drawImage(fuente, -ancho / 2, -alto / 2);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("No se pudo generar la imagen rotada."))),
        mimeType,
        mimeType === "image/jpeg" ? 0.92 : undefined,
      );
    });
  } finally {
    liberar();
  }
};

/** Nombre con la extensión que corresponde al MIME (el backend valida que coincidan). */
const nombreConExtension = (doc: IDocumentoRotable): string => {
  const ext = doc.mimeType === "image/png" ? "png" : "jpg";
  const base = (doc.originalFileName || "documento").replace(/\.[^.]+$/, "") || "documento";
  return `${base}.${ext}`;
};

/**
 * Guarda el documento rotado `giro` grados en sentido horario. Devuelve el nuevo
 * tamaño en bytes. Lanza un Error con mensaje apto para el usuario si falla.
 */
export const rotarDocumento = async (doc: IDocumentoRotable, giro: number): Promise<{ size: number }> => {
  const baseUrl = process.env.REACT_APP_API_APPLICANT;
  const userId = sessionStorage.getItem("usuarioId") ?? "";
  const grados = normalizarGiro(giro);
  if (!baseUrl) throw new Error("Configuración faltante: REACT_APP_API_APPLICANT");
  if (grados === 0) throw new Error("El documento no tiene rotación pendiente.");
  if (!esDocumentoRotable(doc.mimeType)) throw new Error("Solo se pueden rotar imágenes JPG/PNG o documentos PDF.");

  const id = encodeURIComponent(doc.personDocumentId);
  try {
    const fd = new FormData();
    fd.append("degrees", String(grados));

    if (doc.mimeType !== "application/pdf") {
      const original = await fetch(`${baseUrl}/application/document-file/${id}?inline=true`, { cache: "no-store" });
      if (!original.ok) throw new Error(await readApiErrorMessage(original));
      const rotada = await rotarImagen(await original.blob(), grados, doc.mimeType);
      fd.append("file", new File([rotada], nombreConExtension(doc), { type: doc.mimeType }));
    }

    const res = await fetch(
      `${baseUrl}/application/rotate-document/${id}?userId=${encodeURIComponent(userId)}`,
      { method: "POST", body: fd },
    );
    if (!res.ok) throw new Error(await readApiErrorMessage(res));
    marcarArchivoReescrito(doc.personDocumentId);
    const data = (await res.json()) as { size?: number };
    return { size: typeof data?.size === "number" ? data.size : 0 };
  } catch (err) {
    throw new Error(getUserErrorMessage(err, "No se pudo guardar la rotación del documento."));
  }
};
